// LoginAccountContextFlow — consumes loginV2's `session_access` before auth publishes the token.
// That ordering is load-bearing: the shell token watcher starts AccountBootstrapFlow synchronously,
// so the selected account must already exist for the first bootstrap request to use its real id.
import type { AccountItem } from '../models/account.types'
import type { SessionAccessSection } from '../models/bootstrap.types'
import { AccountService } from '../service/AccountService'
import { mapAccounts, mapUser, mapUserAccessInfo } from '../service/BootstrapMapper'
import { useAccountStore, type AccountStore } from '../stores/accountStore'

import { userKeyOf } from './flowToolkit'

export interface LoginAccountContextFlowOptions {
  accountService?: AccountService
  accountStore?: AccountStore
}

export class LoginAccountContextFlow {
  private readonly accountService: AccountService
  private readonly accountStore: AccountStore

  constructor(options: LoginAccountContextFlowOptions = {}) {
    this.accountService = options.accountService ?? new AccountService()
    this.accountStore = options.accountStore ?? useAccountStore()
  }

  /** Hydrate identity/access and return the account the subsequent bootstrap will use. */
  public execute(sessionAccess: SessionAccessSection): AccountItem | null {
    const user = mapUser(sessionAccess)
    const accounts = mapAccounts(sessionAccess) ?? []
    const userAccessInfo = mapUserAccessInfo(sessionAccess)

    if (user) this.accountStore.setUser(user)
    this.accountStore.setAccounts(accounts)
    if (userAccessInfo) this.accountStore.setUserAccessInfo(userAccessInfo)

    const userKey = userKeyOf(user)
    const rememberedAccountId = userKey ? this.accountStore.oldSelectedAccount[userKey] : undefined
    const userId = this.accountStore.userId
    const account = this.accountService.pickAccount({
      accounts,
      ...(userId !== undefined ? { userId } : {}),
      ...(rememberedAccountId !== undefined ? { oldAccountId: rememberedAccountId } : {}),
    })

    this.accountStore.setAccount(account)
    if (account && userKey) {
      this.accountStore.setOldSelectedAccount(userKey, Number(account.id))
    }
    return account
  }
}
